import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useAuthQuery } from './use-auth-query';
import { useBatchedPaginatedQuery } from './use-batched-paginated-query';
import type { BatchFetcherOpts, BatchedPaginationOpts } from './use-batched-paginated-query';

type PropertyQueryOpts = {
  viewPage?: number;
  viewPageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  enabled?: boolean;
};

function buildPropertyQueryOpts(opts: PropertyQueryOpts): BatchedPaginationOpts {
  return {
    viewPage: opts.viewPage ?? 1,
    viewPageSize: opts.viewPageSize ?? 10,
    sortBy: opts.sortBy,
    sortOrder: opts.sortOrder,
    filters: opts.search ? { search: opts.search } : undefined,
    staleTime: 1000 * 60 * 5,
    enabled: opts.enabled,
  };
}

function mapFetchOpts(fetchOpts: BatchFetcherOpts) {
  return {
    page: fetchOpts.page,
    limit: fetchOpts.limit,
    sortBy: fetchOpts.sortBy as string | undefined,
    sortOrder: fetchOpts.sortOrder as 'asc' | 'desc' | undefined,
    search: fetchOpts.search as string | undefined,
  };
}

export function useProperties(opts: PropertyQueryOpts = {}) {
  return useBatchedPaginatedQuery(
    queryKeys.properties.all(),
    (fetchOpts: BatchFetcherOpts) => api.getProperties(mapFetchOpts(fetchOpts)),
    buildPropertyQueryOpts(opts),
  );
}

export function useAllProperties(opts: PropertyQueryOpts = {}) {
  return useBatchedPaginatedQuery(
    queryKeys.properties.adminAll(),
    (fetchOpts: BatchFetcherOpts) => api.getAllProperties(mapFetchOpts(fetchOpts)),
    buildPropertyQueryOpts(opts),
  );
}

export function useProperty(id: string) {
  return useAuthQuery({
    queryKey: queryKeys.properties.detail(id),
    queryFn: () => api.getProperty(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
  });
}

export function usePropertyReports(propertyId: string) {
  return useAuthQuery({
    queryKey: queryKeys.inspections.byProperty(propertyId),
    queryFn: () => api.getPropertyReports(propertyId),
    enabled: !!propertyId,
    staleTime: 1000 * 60 * 2,
  });
}

export function usePropertyActiveTask(propertyId: string) {
  return useAuthQuery({
    queryKey: queryKeys.properties.activeTask(propertyId),
    queryFn: () => api.getPropertyActiveTask(propertyId),
    enabled: !!propertyId,
    staleTime: 1000 * 60 * 2,
  });
}

export function useReport(id: string) {
  return useAuthQuery({
    queryKey: queryKeys.inspections.detail(id),
    queryFn: () => api.getReport(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
  });
}
